import React, { FC, useMemo, useState } from "react";
import useStyles from "./useStyles";
import Box from "../../Unknown/Box";
import AppointmentAIReportsRoleTable from "../AppointmentAIReportsRoleTable";
import { useTableAppointmentAIReports } from "./useTableAppointmentAIReports";
import { GridSortItem } from "../../Unknown/DataGrid";
import { useAppContext } from "../../Unknown/AppContext";
import { DashboardAppointmentAIReport } from "./getAppointmentAIReports";
import AppointmentAIReportReviewEditDialog from "../AppointmentAIReportReviewEditDialog";
import useAppointmentAIReportReviewEditDialog from "../AppointmentAIReportReviewEditDialog/useAppointmentAIReportReviewEditDialog";

type AppointmentAIReportsTableProps = {
  role?: string;
};

const initialSortingModel: GridSortItem[] = [];

const sortAppointmentAIReports = (
  reports: DashboardAppointmentAIReport[],
): DashboardAppointmentAIReport[] => {
  const statReports: DashboardAppointmentAIReport[] = [];
  const priorityReports: DashboardAppointmentAIReport[] = [];
  const otherReports: DashboardAppointmentAIReport[] = [];

  reports.forEach((report) => {
    if (report.appointment.is_stat) {
      statReports.push(report);
    } else if (report.appointment.has_priority) {
      priorityReports.push(report);
    } else {
      otherReports.push(report);
    }
  });

  const sortByDateDesc = (
    a: DashboardAppointmentAIReport,
    b: DashboardAppointmentAIReport,
  ) => {
    const dateA = new Date(a.appointment.time_slot.started_at).getTime();
    const dateB = new Date(b.appointment.time_slot.started_at).getTime();
    return dateB - dateA;
  };

  statReports.sort(sortByDateDesc);
  priorityReports.sort(sortByDateDesc);
  otherReports.sort(sortByDateDesc);

  return [...statReports, ...priorityReports, ...otherReports];
};

const AppointmentAIReportsTableContainer: FC<
  AppointmentAIReportsTableProps
> = ({ role }) => {
  const [isApprovedShown, setIsApprovedShown] = useState(false);
  const [sortModel, setSortModel] =
    useState<GridSortItem[]>(initialSortingModel);

  const { tableRoot } = useStyles();

  const {
    databaseUserData: { centerIds },
  } = useAppContext();

  const { appointmentAIReports, isLoading, refetch } =
    useTableAppointmentAIReports({
      centerIds,
      isApprovedShown,
    });

  const sortedAppointmentAIReports = useMemo(() => {
    return sortAppointmentAIReports(appointmentAIReports);
  }, [appointmentAIReports]);

  const {
    isOpened: isDialogOpened,
    onOpenClick: onDialogOpen,
    props: dialogProps,
  } = useAppointmentAIReportReviewEditDialog({
    reports: sortedAppointmentAIReports,
    refetch,
  });

  const handleReviewClick = (id: number) => {
    onDialogOpen(id, "review");
  };

  const handleEditClick = (id: number) => {
    onDialogOpen(id, "edit");
  };

  const showApprovedProps = {
    checked: isApprovedShown,
    onChange: (event: React.ChangeEvent, checked: boolean) => {
      setIsApprovedShown(checked);
      setSortModel(checked ? [{ field: "time", sort: "desc" }] : []);
    },
  };

  return (
    <>
      <Box className={tableRoot}>
        <AppointmentAIReportsRoleTable
          role={role}
          appointmentAIReports={sortedAppointmentAIReports}
          isLoading={isLoading && !sortedAppointmentAIReports?.length}
          showApprovedProps={showApprovedProps}
          sortModel={sortModel}
          setSortModel={setSortModel}
          onReviewClick={handleReviewClick}
          onEditClick={handleEditClick}
        />
      </Box>
      {dialogProps && isDialogOpened && (
        <AppointmentAIReportReviewEditDialog {...dialogProps} />
      )}
    </>
  );
};

export default AppointmentAIReportsTableContainer;
